/**
 * Utility functions for pet data normalization and extraction
 */

/**
 * Extract species name from species object or string
 * @param {string|object} species - Species as string or object with name property
 * @returns {string} Species name
 */
export const getSpeciesName = (species) => {
  if (!species) return null;
  if (typeof species === 'string') return species;
  if (species && typeof species === 'object' && species.name) return species.name;
  return null;
};

/**
 * Extract breed name from breed object or string
 * @param {string|object} breed - Breed as string or object with name property
 * @returns {string} Breed name
 */
export const getBreedName = (breed) => {
  if (!breed) return null;
  if (typeof breed === 'string') return breed;
  if (breed && typeof breed === 'object' && breed.name) return breed.name;
  return null;
};

/**
 * Get pet name from pet object (handles multilingual names)
 * @param {object} pet - Pet object
 * @returns {string} Pet name
 */
export const getPetName = (pet) => {
  if (!pet) return null;
  if (typeof pet.name === 'string') return pet.name;
  if (pet.name?.en) return pet.name.en;
  if (pet.name?.ar) return pet.name.ar;
  return null;
};

/**
 * Compute a compact age label for a pet (e.g. "8m" or "2y")
 * @param {object} pet - Pet object with birthdate/birth_date or precomputed age fields
 * @returns {string|null} Short age label, or null when unknown
 */
export const getShortAge = (pet) => {
  if (!pet) return null;
  if (typeof pet.age_formatted === 'string' && pet.age_formatted) {
    return pet.age_formatted;
  }

  const raw = pet.birthdate || pet.birth_date;
  if (!raw) return null;

  const birth = new Date(raw);
  if (Number.isNaN(birth.getTime())) return null;

  const today = new Date();
  let years = today.getFullYear() - birth.getFullYear();
  let months = today.getMonth() - birth.getMonth();
  if (months < 0 || (months === 0 && today.getDate() < birth.getDate())) {
    years--;
    months += 12;
  }
  if (years <= 0) {
    return `${Math.max(months, 0)}m`;
  }
  return `${years}y`;
};

/**
 * Normalize pet info array - ensures it's always an array and extracts species/breed names
 * @param {array|object} petInfo - Pet info as single object, array, or nested in pets array
 * @returns {array} Normalized array of pet objects with extracted species/breed names
 */
export const normalizePetInfo = (petInfo) => {
  if (!petInfo) return [];
  
  // Handle different data structures
  let petInfoArray = petInfo;
  if (!Array.isArray(petInfo)) {
    petInfoArray = [petInfo];
  }
  
  // Normalize each pet object
  return petInfoArray.map(pet => {
    const species = getSpeciesName(pet.species);
    const breed = getBreedName(pet.breed);
    
    return {
      ...pet,
      species: species || pet.species,
      breed: breed || pet.breed
    };
  });
};
